import React from 'react';
import ConfigMenu from '../components/ConfigMenu';
import {
  Box,
  Layers,
  RefreshCcw,
  Settings,
  Tag,
  Calculator,
  ChevronRight,
  ArrowLeft,
  Package,
  Palette
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Card de Configuración - Diseño Enterprise (consistente con Productos)
 */
const ConfigCard = ({ icon: Icon, title, desc, colorClass, bgClass, onClick }) => (
  <div
    className="group bg-white p-6 rounded-2xl border border-slate-200 shadow-sm hover:border-[#51A7FD] hover:shadow-md transition-all cursor-pointer relative overflow-hidden"
    onClick={onClick}
    role="button"
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onClick?.(); }}
  >
    <div className="flex items-start gap-4">
      <div className={`p-3 rounded-xl ${bgClass} ${colorClass} transition-colors shrink-0`}>
        <Icon size={20} />
      </div>
      <div className="min-w-0 pr-4">
        <h6 className="text-[11px] font-bold text-slate-800 uppercase tracking-widest mb-1 group-hover:text-[#51A7FD] transition-colors">
          {title}
        </h6>
        <p className="text-[10px] text-slate-400 font-medium leading-relaxed italic">
          {desc}
        </p>
      </div>
    </div>
    <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all">
      <ChevronRight size={16} className="text-[#51A7FD]" />
    </div>
  </div>
);

function ConfigProductosStock() {
  const navigate = useNavigate();
  // Talles y colores: la tarjeta solo aparece con el lógico activo en Datos de empresa.
  const { organization } = useAuth();
  const talleColorActivo = Boolean(organization?.enable_variant_stock);

  return (
    <div className="w-full min-h-screen bg-[#F8FAFC] font-sans text-slate-700 p-4 md:p-6 lg:p-8">
      {/* HEADER */}
      <header className="flex items-center gap-4 mb-10 px-2">
        <button
          onClick={() => navigate('/sistema')}
          className="p-2.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-[#51A7FD] transition-all shadow-sm"
          title="Volver"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-800 uppercase tracking-tight leading-none">
            Configuración del Sistema
          </h1>
          <p className="text-[10px] font-medium text-slate-400 uppercase tracking-widest mt-2 italic">
            Parámetros maestros de productos, stock y catálogos
          </p>
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 max-w-[1600px] mx-auto animate-nova-in">
        {/* MENÚ */}
        <aside className="lg:col-span-3">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-2">
            <ConfigMenu />
          </div>
        </aside>

        {/* CONTENIDO */}
        <main className="lg:col-span-9 space-y-8">
          <div className="flex items-center gap-3 px-2">
            <Package size={18} className="text-[#51A7FD]" />
            <h4 className="text-xs font-bold text-slate-500 uppercase tracking-[0.2em]">
              Productos y Stock
            </h4>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            <ConfigCard
              icon={Box}
              title="Productos"
              desc="Gestión del catálogo, precios y atributos. Acceso al módulo operativo."
              colorClass="text-[#51A7FD]"
              bgClass="bg-blue-50"
              onClick={() => navigate('/sistema/productos')}
            />

            <ConfigCard
              icon={Tag}
              title="Categorías"
              desc="Clasificación del catálogo para filtros, reportes y tienda."
              colorClass="text-orange-500"
              bgClass="bg-orange-50"
              onClick={() => navigate('/sistema/categorias')}
            />

            {talleColorActivo && (
              <ConfigCard
                icon={Palette}
                title="Talles y colores"
                desc="Curvas de talles, colores y stock por talle para indumentaria y calzado."
                colorClass="text-pink-600"
                bgClass="bg-pink-50"
                onClick={() => navigate('/sistema/config-talles-colores')}
              />
            )}

            <ConfigCard
              icon={Calculator}
              title="Unidades de Medida"
              desc="Unidades, abreviaturas y estandarización de carga."
              colorClass="text-slate-700"
              bgClass="bg-slate-100"
              onClick={() => navigate('/sistema/unidades-medida')}
            />

            <ConfigCard
              icon={Layers}
              title="Stock Actual"
              desc="Niveles de stock por sitio y alertas de mínimo."
              colorClass="text-emerald-600"
              bgClass="bg-emerald-50"
              onClick={() => navigate('/sistema/inventario')}
            />

            <ConfigCard
              icon={RefreshCcw}
              title="Transferencias"
              desc="Movimientos entre sitios y depósitos virtuales."
              colorClass="text-violet-600"
              bgClass="bg-violet-50"
              onClick={() => navigate('/sistema/inventario-transferencia')}
            />

            <ConfigCard
              icon={Settings}
              title="Ajustes de Stock"
              desc="Regularizaciones, inventarios físicos y motivos."
              colorClass="text-[#F05A57]"
              bgClass="bg-red-50"
              onClick={() => navigate('/sistema/inventario-ajuste')}
            />
          </div>
        </main>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

export default ConfigProductosStock;
